import {Component, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {PANEL_CARD_CUSTOMER, PANEL_CARD_DEVICES} from '@shared/constants';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NbCardModule, NbSpinnerModule} from '@nebular/theme';
import {PanelGridControlComponent} from '../panel-grid-control/panel-grid-control.component';
import {SharedModule} from '@shared/shared.module';
import {MkadCheckLevelType} from '@shared/enums';
import {UserLevelEnum, ZbAuthCheckerService} from '@zibanu/auth';
import {MkadBaseComponent} from '@shared/components';
import {Router} from '@angular/router';
import {PanelCardControlComponent} from '../panel-card-control/panel-card-control.component';

@Component({
  selector: 'mkad-customers-admin-panels',
  standalone: true,
  imports: [
    TranslateModule,
    NbCardModule,
    PanelGridControlComponent,
    SharedModule,
    NbSpinnerModule,
    PanelCardControlComponent
  ],
  template: `
    <section class="flex flex-column gap-3 pt-1 pb-2">
      <h3 class="my-0 pl-2">{{ 'app.setting.panel.admin.customer.title' | translate }}</h3>
      <mkad-panel-grid-control>
        <ng-container cards>
          @for (item of cardListCustomer(); track item) {
            <nb-card class="relative cursor-pointer mid_shape_card mb-0 p-3 inline-flex"
                     mkadCheckLevel [elementType]="levelType.div" [minLevel]="item.data.level"
                     (mkadClick)="onClickCard(item.link ?? '')" [nbSpinner]="isLoading">

              <mkad-panel-card-control [item]="item"/>
            </nb-card>
          }
        </ng-container>
      </mkad-panel-grid-control>
    </section>
  `,
  styleUrl: './customers-admin-panel.component.scss',
})
export class CustomersAdminPanelsComponent extends MkadBaseComponent implements OnInit {
  readonly cardListCustomer = signal(PANEL_CARD_CUSTOMER);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zbAuthChecker: ZbAuthCheckerService = inject(ZbAuthCheckerService);
  private readonly router: Router = inject(Router);
  protected readonly levelType = MkadCheckLevelType;

  /**
   * @description
   * Constructor class
   * @param translateService TranslateService dependency injection
   */
  constructor(protected readonly translateService: TranslateService) {
    super(translateService);
    this.translate.onLangChange
      .pipe(
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => this.translateCardList(),
      });
  }

  /**
   * @description
   * Method that response to OnInit
   */
  override ngOnInit(): void {
    super.ngOnInit();
    this.translateCardList();
    this.isLoading = false;
  }

  /**
   * @description
   * Method that translates card list
   */
  private translateCardList(): void {
    const customerToTranslate = structuredClone(PANEL_CARD_CUSTOMER);
    const deviceToTranslate = structuredClone(PANEL_CARD_DEVICES);
    const customerTranslated = this.getTranslateMap(customerToTranslate, ['title']);
    const deviceTranslated = this.getTranslateMap(deviceToTranslate, ['title']);
    this.cardListCustomer.set([...customerTranslated, ...deviceTranslated]);
  }

  /**
   * @description
   * Method that response to click on the card
   * @param {string} route
   */
  onClickCard(route: string): void {
    this.isLoading = true;
    this.zbAuthChecker.isGranted(UserLevelEnum.Guest)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (valueGranted: boolean) => {
          if (valueGranted) {
            this.router.navigate(['settings/' + route]).then();
          } else {
            this.showError('No autorizado...');
          }
          this.isLoading = false;
        },
        error: (error) => this.handleErrorResponse(error),
      });
  }
}
